import React, { useState, useEffect } from 'react';
import { ArrowLeft, Zap, CheckCircle, AlertTriangle, Copy, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { BeneficiaryList } from './shared/BeneficiaryList';
import { BeneficiaryModal } from './shared/BeneficiaryModal';
import { ConfirmationModal } from './shared/ConfirmationModal';
import { AmountInput } from './shared/AmountInput';
import { GlobalPinModal } from './shared/GlobalPinModal';
import { PaymentProceedModal, type PaymentProceedPayload } from './shared/PaymentProceedModal';
import { randsToCents } from '../../utils/formatCurrency';
import { apiService } from '../../services/apiService';
import { ErrorModal } from '../ui/ErrorModal';
import { BrandSpinner } from '../common/LoadingSpinner';
import {
  beneficiaryService,
  electricityService,
  generateIdempotencyKey,
  formatCurrency,
  validateMeterNumber,
  type Beneficiary,
  type ElectricityCatalog,
  type PurchaseResult
} from '../../services/overlayService';
import { formatPinForDisplay, pinToClipboardValue } from '../../utils/formatPin';

interface ElectricityBeneficiary extends Beneficiary {
  // Uses accountType from base Beneficiary interface
}

type Step = 'beneficiary' | 'amount' | 'confirm';
type LoadingState = 'idle' | 'loading' | 'success' | 'error';

export function ElectricityOverlay() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('beneficiary');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<ElectricityBeneficiary | null>(null);
  const [editingBeneficiary, setEditingBeneficiary] = useState<ElectricityBeneficiary | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [catalog, setCatalog] = useState<ElectricityCatalog | null>(null);
  const [beneficiaries, setBeneficiaries] = useState<ElectricityBeneficiary[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionRef, setTransactionRef] = useState<string>('');
  const [electricityToken, setElectricityToken] = useState<string>('');
  const [tokenCopied, setTokenCopied] = useState(false);

  const handleCopyToken = async () => {
    if (!electricityToken) return;
    try {
      await navigator.clipboard.writeText(pinToClipboardValue(electricityToken));
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (e) {
      console.error('Copy token failed:', e);
    }
  };
  const [error, setError] = useState<string>('');
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  // Universal pre-debit proceed confirmation (replaces the legacy ConfirmSheet step).
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [proceedPayload, setProceedPayload] = useState<PaymentProceedPayload | null>(null);
  const [beneficiaryToRemove, setBeneficiaryToRemove] = useState<ElectricityBeneficiary | null>(null);
  const [beneficiaryIsMyMoolahUser, setBeneficiaryIsMyMoolahUser] = useState(false);
  const [bestProductId, setBestProductId] = useState<string | null>(null);
  const [showEeziPowerModal, setShowEeziPowerModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalTitle, setErrorModalTitle] = useState('Purchase Failed');
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Load beneficiaries on mount
  useEffect(() => {
    loadBeneficiaries();
  }, []);

  const loadBeneficiaries = async () => {
    try {
      setLoadingState('loading');
      const electricityBeneficiaries = await beneficiaryService.getBeneficiaries('electricity');
      setBeneficiaries(electricityBeneficiaries as ElectricityBeneficiary[]);
      setLoadingState('idle');
    } catch (err) {
      console.error('Failed to load beneficiaries:', err);
      setError('Failed to load beneficiaries');
      setLoadingState('error');
    }
  };

  const handleBeneficiarySelect = (beneficiary: any, accountId?: number): void => {
    void (async () => {
      try {
        setLoadingState('loading');
        const normalized = {
          ...(beneficiary as any),
          id: beneficiary.id != null ? String(beneficiary.id) : ''
        } as Beneficiary;
        setSelectedBeneficiary(normalized);
        
        // Load catalog for selected beneficiary
        const catalogData = await electricityService.getCatalog(normalized.id);
        setCatalog(catalogData);

        // Store the winning product variant ID (first product is the best-commission winner from v_best_offers)
        if (catalogData.products && catalogData.products.length > 0) {
          setBestProductId(catalogData.products[0].id);
        }
        
        setCurrentStep('amount');
        setLoadingState('idle');
      } catch (err) {
        console.error('Failed to load catalog:', err);
        setError('Failed to load electricity catalog');
        setLoadingState('error');
      }
    })();
  };

  const catalogMin = catalog?.minAmount || 30;
  const issuingToken = currentStep === 'amount' && loadingState === 'loading';
  // Matches ELECTRICITY_CUSTOM_MAX_RAND in services/electricityAmountPolicy.js.
  // The loaded catalog maxAmount is the source of truth once the catalog returns.
  const catalogMax = catalog?.maxAmount || 5000;
  const numericAmount = amount ? parseFloat(amount) : NaN;
  const amountOutOfRange = !amount
    || Number.isNaN(numericAmount)
    || numericAmount < catalogMin
    || numericAmount > catalogMax;

  const handleAmountNext = () => {
    if (!amountOutOfRange) {
      // Build the proceed payload and open the universal confirm modal
      // instead of rendering the legacy inline ConfirmSheet step.
      const estimatedUnits = (numericAmount * 1.2).toFixed(1);
      const notices: PaymentProceedPayload['notices'] = [];
      if (selectedBeneficiary?.metadata?.isValid === false) {
        notices.push({
          type: 'warning',
          title: 'Meter validation required',
          description: 'This meter number needs to be verified before purchase.',
        });
      }
      notices.push({
        type: 'info',
        title: 'Token delivery',
        description: `Your electricity token will be displayed on screen and saved in your notifications. Est. ${estimatedUnits} kWh.`,
      });
      setProceedPayload({
        amountCents: randsToCents(numericAmount),
        recipientLabel: selectedBeneficiary?.name || 'this meter',
        recipientSubLabel: selectedBeneficiary?.identifier
          ? `Meter ${selectedBeneficiary.identifier}`
          : undefined,
        contextLabel: 'Electricity token purchase',
        notices,
        title: 'Confirm electricity purchase',
        proceedButtonText: 'Yes, buy token',
      });
      setShowProceedModal(true);
    }
  };

  const cancelProceed = () => {
    setProceedPayload(null);
    setShowProceedModal(false);
  };

  const handleConfirmTransaction = async () => {
    if (!selectedBeneficiary || !amount) return;

    try {
      setLoadingState('loading');
      setError('');
      setShowProceedModal(false);
      setProceedPayload(null);
      
      const idempotencyKey = generateIdempotencyKey();
      
      const purchasePayload: Parameters<typeof electricityService.purchase>[0] = {
        beneficiaryId: selectedBeneficiary.id,
        amount: parseFloat(amount),
        idempotencyKey
      };
      if (bestProductId) {
        purchasePayload.productId = bestProductId;
      }
      const result = await electricityService.purchase(purchasePayload);
      
      setTransactionRef(result.reference);
      setElectricityToken(result.token || `${Math.random().toString().slice(2, 6)}-${Math.random().toString().slice(2, 6)}-${Math.random().toString().slice(2, 6)}-${Math.random().toString().slice(2, 6)}`);
      setBeneficiaryIsMyMoolahUser(result.beneficiaryIsMyMoolahUser || false);
      setLoadingState('success');
      setShowSuccess(true);
    } catch (err: any) {
      console.error('Purchase failed:', err);
      const responseData = err.response?.data;
      const isTimeout = responseData?.isTimeout || responseData?.errorCode?.includes('TIMEOUT');
      const isMeterMin = responseData?.errorCode === 'METER_MIN_AMOUNT';
      
      if (isTimeout) {
        setErrorModalTitle('Provider Slow to Respond');
        setErrorModalMessage(
          'The electricity provider is taking too long to respond. Your wallet has not been charged. Please try again in a few minutes.'
        );
      } else if (isMeterMin) {
        const minAmount = responseData?.minimumAmount;
        setErrorModalTitle('Minimum Amount Required');
        setErrorModalMessage(
          minAmount
            ? `This meter requires a minimum purchase of R${parseFloat(minAmount).toFixed(2)}, likely due to outstanding utility charges. Your wallet has not been charged. You can use your bank app to make partial debt payments to reduce this minimum.`
            : (responseData?.message || 'The amount is below the minimum purchase amount for this meter. Please try a higher amount.')
        );
      } else {
        setErrorModalTitle('Purchase Failed');
        setErrorModalMessage(
          responseData?.message || 'Something went wrong while processing your electricity purchase. Your wallet has not been charged. Please try again.'
        );
      }
      setShowErrorModal(true);
      setLoadingState('idle');
    }
  };

  const handleAddNewBeneficiary = () => {
    setShowBeneficiaryModal(true);
  };

  const handleBeneficiaryCreated = (newBeneficiary: Beneficiary) => {
    // Add the new beneficiary to the list and reload
    setBeneficiaries(prev => [...prev, newBeneficiary as ElectricityBeneficiary]);
    setShowBeneficiaryModal(false);
  };

  // Handlers accept any beneficiary from BeneficiaryList; cast when storing locally
  const handleEditBeneficiary = (beneficiary: any): void => {
    setEditingBeneficiary(beneficiary as Beneficiary);
    setShowBeneficiaryModal(true);
  };

  const handleRemoveBeneficiary = (beneficiary: any): void => {
    setBeneficiaryToRemove(beneficiary as Beneficiary);
    setShowConfirmationModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!beneficiaryToRemove) return;
    
    try {
      // Banking-grade: Remove only electricity service accounts, not the entire beneficiary
      // This allows the beneficiary to still exist for other services (e.g., if they have airtime/data)
      // Never affects their user account if they're a registered MyMoolah user
      await beneficiaryService.removeAllServicesOfType(beneficiaryToRemove.id, 'electricity');
      
      // Refresh beneficiaries list
      loadBeneficiaries();
      // Clear selection if this was the selected beneficiary
      if (selectedBeneficiary?.id === beneficiaryToRemove.id) {
        setSelectedBeneficiary(null);
      }
      setBeneficiaryToRemove(null);
    } catch (error) {
      console.error('Failed to remove beneficiary services:', error);
      alert('Failed to remove recipient. Please try again.');
    }
  };

  const getSummaryRows = () => {
    if (!selectedBeneficiary || !amount) return [];
    
    const numericAmount = parseFloat(amount);
    const estimatedUnits = (numericAmount * 1.2).toFixed(1); // Mock rate
    
    return [
      {
        label: 'Meter',
        value: `${selectedBeneficiary.name}`
      },
      {
        label: 'Meter Number',
        value: selectedBeneficiary.identifier
      },
      {
        label: 'Meter Type',
        value: selectedBeneficiary.metadata?.meterType || 'Prepaid'
      },
      {
        label: 'Amount',
        value: formatCurrency(numericAmount)
      },
      {
        label: 'Est. Units',
        value: `${estimatedUnits} kWh`
      },
      {
        label: 'Total',
        value: formatCurrency(numericAmount),
        highlight: true
      }
    ];
  };

  const getConfirmNotices = () => {
    const notices = [];
    
    if (selectedBeneficiary?.metadata?.isValid === false) {
      notices.push({
        id: 'invalid-meter',
        type: 'error' as const,
        title: 'Meter Validation Required',
        description: 'This meter number needs to be verified before purchase'
      });
    }
    
    notices.push({
      id: 'token-delivery',
      type: 'info' as const,
      title: 'Token Delivery',
      description: 'Your electricity token will be displayed on screen and saved in your notifications'
    });
    
    return notices;
  };

  const getEstimatedUnits = (currentAmount: string): string => {
    const numericAmount = parseFloat(currentAmount);
    if (isNaN(numericAmount)) return '';
    
    const units = (numericAmount * 1.2).toFixed(1);
    return `${units} kWh`;
  };

  if (showSuccess) {
    return (
      <div style={{ padding: '1rem' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/transact')}
            style={{
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px'
            }}
          >
            <ArrowLeft style={{ width: '20px', height: '20px' }} />
          </Button>
          
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            Token Generated
          </h1>
          
          <div style={{ width: '44px' }}></div>
        </div>

        {/* Token Card */}
        <Card style={{
          backgroundColor: '#ffffff',
          border: '1px solid #16a34a',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(22, 163, 74, 0.1)'
        }}>
          <CardHeader style={{
            backgroundColor: '#16a34a',
            color: '#ffffff',
            borderRadius: '12px 12px 0 0'
          }}>
            <div className="flex items-center gap-3">
              <CheckCircle style={{ width: '24px', height: '24px' }} />
              <div>
                <CardTitle style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ffffff'
                }}>
                  Token Ready
                </CardTitle>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#ffffff',
                  opacity: 0.9
                }}>
                  {new Date().toLocaleString()}
                </p>
                {beneficiaryIsMyMoolahUser && (
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '10px',
                    color: '#ffffff',
                    opacity: 0.8,
                    marginTop: '4px'
                  }}>
                    ✅ Recipient notified via MyMoolah
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent style={{ padding: '1rem' }}>
            <div className="space-y-4">
              {/* Electricity Token Display */}
              <div style={{
                padding: '20px',
                backgroundColor: '#f8fafe',
                borderRadius: '14px',
                border: '2px dashed #86BE41',
                textAlign: 'center'
              }}>
                <Zap style={{
                  width: '28px',
                  height: '28px',
                  color: '#f59e0b',
                  margin: '0 auto 8px'
                }} />
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '11px',
                  color: '#6b7280',
                  margin: '0 0 12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600
                }}>
                  Your Electricity Token
                </p>
                <p style={{
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#1f2937',
                  letterSpacing: '3px',
                  wordBreak: 'break-word',
                  margin: '0 0 16px',
                  lineHeight: 1.4
                }}>
                  {formatPinForDisplay(electricityToken)}
                </p>
                <Button
                  onClick={handleCopyToken}
                  style={{
                    backgroundColor: tokenCopied ? '#16a34a' : '#86BE41',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '10px 20px',
                    minHeight: '44px',
                    transition: 'background-color 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Copy style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  {tokenCopied ? 'Copied!' : 'Copy Token'}
                </Button>
              </div>

              {/* Transaction Details */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Reference
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {transactionRef}
                  </span>
                </div>
                
                {getSummaryRows().map((row, index) => (
                  <div key={index} className="flex justify-between">
                    <span style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      {row.label}
                    </span>
                    <span style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: row.highlight ? '700' : '500',
                      color: '#1f2937'
                    }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <Button
                onClick={() => navigate('/transact')}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  minHeight: '44px'
                }}
              >
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', position: 'relative' }}>
        <Button
          variant="ghost"
          onClick={() => navigate('/transact')}
          style={{
            padding: '8px',
            minWidth: '44px',
            minHeight: '44px',
            position: 'absolute',
            left: '0',
            zIndex: 1
          }}
        >
          <ArrowLeft style={{ width: '20px', height: '20px' }} />
        </Button>
        
        <div style={{ 
          flex: 1, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            Electricity
          </h1>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '12px',
            color: '#6b7280',
            margin: 0
          }}>
            Prepaid tokens delivered instantly.
          </p>
        </div>
      </div>

      {/* Step 1: Beneficiary Selection */}
      {currentStep === 'beneficiary' && (
        <div>
          <Card style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '1rem'
          }}>
            <CardHeader>
              <CardTitle style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '16px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Select Meter
              </CardTitle>
            </CardHeader>
          </Card>

          <BeneficiaryList
            type="electricity"
            beneficiaries={beneficiaries}
            selectedBeneficiary={selectedBeneficiary}
            onSelect={handleBeneficiarySelect}
            onAddNew={handleAddNewBeneficiary}
            onEdit={handleEditBeneficiary}
            onRemove={handleRemoveBeneficiary}
            searchPlaceholder="Search meter name or number"
            isLoading={loadingState === 'loading'}
            showFilters={false}
          />

          {/* eeziPower — PIN electricity voucher, no meter number required */}
          <div
            onClick={() => setShowEeziPowerModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginTop: '12px',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.backgroundColor = '#fffbeb'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            <div className="flex items-center gap-3">
              <div style={{ width: '40px', height: '40px', backgroundColor: '#f59e0b', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Zap style={{ width: '20px', height: '20px', color: '#ffffff' }} />
              </div>
              <div>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                  eeziPower
                </p>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  PIN electricity voucher · Flash · ZAR
                </p>
              </div>
            </div>
            <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: '600', color: '#f59e0b', margin: 0 }}>
              Buy PIN →
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Amount Input */}
      {currentStep === 'amount' && selectedBeneficiary && catalog && (
        <div className="space-y-4">
          {issuingToken && (
            <Card style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <CardContent style={{ paddingTop: '2rem', paddingBottom: '2rem', textAlign: 'center' }}>
                <BrandSpinner
                  size={56}
                  label="Issuing your electricity token…"
                  subtitle="Please keep this screen open. This usually takes 10–20 seconds."
                />
              </CardContent>
            </Card>
          )}
          {/* Selected Meter Summary */}
          <Card style={{
            backgroundColor: '#f8fafe',
            border: '1px solid #86BE41',
            borderRadius: '12px'
          }}>
            <CardContent style={{ padding: '1rem' }}>
              <div className="flex items-center gap-3">
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#86BE41',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Zap style={{ width: '20px', height: '20px', color: '#ffffff' }} />
                </div>
                <div>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {selectedBeneficiary.name}
                  </p>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    {selectedBeneficiary.identifier} • {selectedBeneficiary.metadata?.meterType || 'Prepaid'}
                  </p>
                </div>
                {selectedBeneficiary.metadata?.isValid === false && (
                  <AlertTriangle style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Amount Input Card */}
          <Card style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px'
          }}>
            <CardContent style={{ padding: '1rem' }}>
              <AmountInput
                label="Purchase Amount"
                value={amount}
                onChange={setAmount}
                currency="ZAR"
                currencySymbol="R"
                min={catalogMin}
                max={catalogMax}
                suggestedAmounts={catalog.suggestedAmounts}
                placeholder="0.00"
                helperText={`Minimum R${catalogMin}, Maximum R${catalogMax}`}
                showEstimatedUnits={true}
                estimatedUnits={getEstimatedUnits(amount)}
                required={true}
              />
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCurrentStep('beneficiary')}
              disabled={issuingToken}
              style={{
                flex: '1',
                minHeight: '44px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '12px'
              }}
            >
              Back
            </Button>
            
            <Button
              onClick={handleAmountNext}
              disabled={issuingToken || amountOutOfRange || selectedBeneficiary?.metadata?.isValid === false}
              style={{
                flex: '2',
                minHeight: '44px',
                background: (issuingToken || amountOutOfRange || selectedBeneficiary?.metadata?.isValid === false)
                  ? '#e2e8f0'
                  : 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                color: (issuingToken || amountOutOfRange || selectedBeneficiary?.metadata?.isValid === false)
                  ? '#6b7280'
                  : '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm — now rendered as the universal PaymentProceedModal
          overlay (opened by handleAmountNext). The legacy inline ConfirmSheet
          step has been removed in favour of a single pre-debit confirm. */}

      {/* Universal pre-debit proceed confirmation. */}
      <PaymentProceedModal
        open={showProceedModal}
        payload={proceedPayload}
        onProceed={handleConfirmTransaction}
        onCancel={cancelProceed}
        isProcessing={loadingState === 'loading'}
      />

      {/* Beneficiary Modal */}
      <BeneficiaryModal
        isOpen={showBeneficiaryModal}
        onClose={() => {
          setShowBeneficiaryModal(false);
          setEditingBeneficiary(null);
        }}
        type="electricity"
        editBeneficiary={editingBeneficiary}
        onSuccess={handleBeneficiaryCreated}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
          setBeneficiaryToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Recipient"
        message="Are you sure you want to remove"
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={beneficiaryToRemove?.name}
      />

      {/* eeziPower PIN Modal — electricity voucher, no meter required */}
      {showEeziPowerModal && (
        <GlobalPinModal
          products={[
            { id: 'eezipower_1000', name: 'eeziPower R10', price: 1000, supplierCode: 'FLASH' },
            { id: 'eezipower_2000', name: 'eeziPower R20', price: 2000, supplierCode: 'FLASH' },
            { id: 'eezipower_5000', name: 'eeziPower R50', price: 5000, supplierCode: 'FLASH' },
            { id: 'eezipower_10000', name: 'eeziPower R100', price: 10000, supplierCode: 'FLASH' },
            { id: 'eezipower_20000', name: 'eeziPower R200', price: 20000, supplierCode: 'FLASH' },
            { id: 'eezipower_50000', name: 'eeziPower R500', price: 50000, supplierCode: 'FLASH' },
          ]}
          selectedAccountId={null}
          onClose={() => setShowEeziPowerModal(false)}
          title="eeziPower"
          subtitle="Buy PIN · Electricity voucher · Any meter"
          currency="ZAR"
          confirmHint="An electricity PIN voucher will be generated instantly. Use it to load prepaid electricity on any supported meter."
          onPurchase={(amountCents, idempotencyKey) =>
            apiService.purchaseEeziPower(amountCents, idempotencyKey)
          }
        />
      )}

      {/* Error Modal — user-friendly error messages for failed purchases */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalTitle}
        message={errorModalMessage}
        type="warning"
      />
    </div>
  );
}